import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useLang } from "@/context/LanguageContext";

const LOGO = "https://customer-assets-rejwkqb3.emergentagent.net/job_guest-retreat-finder/artifacts/ymwgc3ta_Logo%20Quattro%20Accommodations.png";

export default function PublicHeader() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { t } = useLang();

  const OWNER_LINKS = [
    { label: t("nav.owners"), path: "/proprietari", testId: "nav-link-proprietari" },
    { label: t("nav.prices"), path: "/prezzi", testId: "nav-link-prezzi" },
    { label: t("nav.faq"), path: "/faq", testId: "nav-link-faq" },
    { label: t("nav.contact"), path: "/contatti", testId: "nav-link-contatti" },
  ];
  const ownerActive = OWNER_LINKS.some((l) => l.path === pathname);

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/90 border-b border-brand-plum/10 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo-link" className="flex items-center shrink-0">
          <img src={LOGO} alt="Quattro Accommodations" className="h-9 md:h-11 w-auto object-contain" />
        </Link>

        <div className="hidden lg:flex items-center gap-7 ml-auto">
          <Link
            to="/case-vacanze"
            data-testid="nav-link-case-vacanze"
            className={`text-sm font-medium tracking-tight transition-colors hover:text-brand-teal ${pathname === "/case-vacanze" ? "text-brand-teal" : "text-brand-plum"}`}
          >
            {t("nav.case")}
          </Link>

          <div className="relative group">
            <button
              data-testid="nav-owners-group"
              className={`flex items-center gap-1 text-sm font-medium tracking-tight transition-colors hover:text-brand-teal ${ownerActive ? "text-brand-teal" : "text-brand-plum"}`}
            >
              {t("nav.ownersArea")} <ChevronDown size={15} className="transition-transform group-hover:rotate-180" />
            </button>
            <div className="absolute right-0 top-full pt-3 hidden group-hover:block">
              <div className="w-56 rounded-xl bg-white border border-brand-plum/10 shadow-lg p-2" data-testid="nav-owners-dropdown">
                {OWNER_LINKS.map((l) => (
                  <Link
                    key={l.path}
                    to={l.path}
                    data-testid={l.testId}
                    className={`block px-3 py-2 rounded-lg text-sm transition-colors hover:bg-brand-tealLight ${pathname === l.path ? "text-brand-teal font-medium" : "text-brand-plum"}`}
                  >
                    {l.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <LanguageSwitcher />
          <Link to="/admin/login" data-testid="nav-admin-login-btn">
            <Button variant="ghost" className="text-brand-plum hover:text-brand-teal hover:bg-brand-tealLight">
              {t("nav.admin")}
            </Button>
          </Link>
          <Link to="/case-vacanze" data-testid="nav-explore-btn">
            <Button className="rounded-full bg-brand-plum hover:bg-brand-violet text-white px-6">
              {t("nav.explore")}
            </Button>
          </Link>
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <LanguageSwitcher />
          <button
            className="text-brand-plum"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-brand-plum/10 bg-white px-4 py-4 space-y-2 animate-fade-in">
          <Link
            to="/case-vacanze"
            onClick={() => setOpen(false)}
            data-testid="nav-link-case-vacanze-mobile"
            className="block py-2 text-brand-plum font-medium"
          >
            {t("nav.case")}
          </Link>
          <div className="pt-2 border-t border-brand-plum/10">
            <p className="text-xs font-mono uppercase tracking-wider text-brand-plum/40 pt-2 pb-1">{t("nav.ownersArea")}</p>
            {OWNER_LINKS.map((l) => (
              <Link
                key={l.path}
                to={l.path}
                onClick={() => setOpen(false)}
                data-testid={`${l.testId}-mobile`}
                className="block py-2 pl-3 text-brand-plum font-medium"
              >
                {l.label}
              </Link>
            ))}
          </div>
          <div className="pt-2 flex flex-col gap-2">
            <Link to="/admin/login" onClick={() => setOpen(false)}>
              <Button variant="outline" className="w-full border-brand-plum/20 text-brand-plum">{t("nav.admin")}</Button>
            </Link>
            <Link to="/case-vacanze" onClick={() => setOpen(false)}>
              <Button className="w-full rounded-full bg-brand-plum text-white">{t("nav.explore")}</Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
